import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { startCase } from "lodash";
import { SharedAccordion } from "@/components/common/shared-accordion";
import { ENUM_PERFORMANCE_CYCLE_TYPE } from "@/utils/enums/performance.enum";
import type { IPerformanceCycleFilters } from "@/types/(company)/performance.interface";

interface Props {
  filters: IPerformanceCycleFilters;
  onChange: (newFilters: IPerformanceCycleFilters) => void;
}

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) =>
  String(currentYear - i),
);

const PerformanceCycleFilter = ({ filters, onChange }: Props) => {
  const { t } = useTranslation(["common", "company", "my-space"]);
  const handleClear = (
    key: keyof IPerformanceCycleFilters,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    onChange({ ...filters, [key]: "" });
  };

  return (
    <div>
      <SharedAccordion
        type="multiple"
        expanded
        items={[
          {
            label: t("company:performance.cycle_type_label"),
            suffix: (
              <ClearButton onClick={(e) => handleClear("cycleType", e)} />
            ),
            content: (
              <Select
                value={filters.cycleType}
                onValueChange={(val) =>
                  onChange({ ...filters, cycleType: val })
                }
              >
                <SelectTrigger className="h-min w-full">
                  <SelectValue
                    placeholder={t("my-space:shared.select_cycle_type")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>
                      {t("company:performance.cycle_type_label")}
                    </SelectLabel>
                    <SelectItem value="all">{t("my-space:ui.all")}</SelectItem>
                    {Object.values(ENUM_PERFORMANCE_CYCLE_TYPE).map((type) => (
                      <SelectItem key={type} value={type}>
                        {startCase(type)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            ),
          },
          {
            label: t("company:performance.year_label"),
            suffix: <ClearButton onClick={(e) => handleClear("year", e)} />,
            content: (
              <Select
                value={filters.year}
                onValueChange={(val) => onChange({ ...filters, year: val })}
              >
                <SelectTrigger className="h-min w-full">
                  <SelectValue
                    placeholder={t("my-space:payroll.select_year")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>
                      {t("company:performance.year_label")}
                    </SelectLabel>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            ),
          },
        ]}
      />
    </div>
  );
};

export default PerformanceCycleFilter;

// --- Sub-components ---

const ClearButton = ({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void;
}) => {
  const { t } = useTranslation(["common"]);
  return (
    <Button
      variant="link"
      size="sm"
      className="text-sm text-red-500 font-normal underline"
      onClick={onClick}
    >
      {t("common:buttons.clear")}
    </Button>
  );
};
